import {
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  CardContent,
  Form,
  FormSubmitButton,
  TextAreaFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { Skeleton } from "@/components/ui/shadcn/Skeleton/Skeleton";
import { useUser } from "@/components/UserProvider";
import {
  retrieveMatterIntakeTemplateQuestionsOptions,
  useDeleteMatterIntakeTemplateQuestion,
} from "@/lib/data/react-query/matters/matter-intake-template-question";
import {
  useCreateMatterIntakeTemplate,
  useDeleteMatterIntakeTemplate,
  useUpdateMatterIntakeTemplate,
} from "@/lib/data/react-query/matters/matter-intake-templates";
import { MatterIntakeTemplateSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type MatterIntakeTemplate,
  type MatterIntakeTemplateQuestion,
} from "@/lib/types";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Pencil, PlusCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";
import { AddOrEditMatterIntakeTemplateQuestionModal } from "./AddOrEditMatterIntakeTemplateQuestionModal";

interface AddOrEditMatterIntakeTemplateModalProps {
  editing: boolean;
  open: boolean;
  setEditing: (editing: boolean) => void;
  setSelectedTemplateId: (templateId: string) => void;
  setOpen: (open: boolean) => void;
  template?: MatterIntakeTemplate;
  isReadOnly?: boolean;
}

const formSchema = MatterIntakeTemplateSchema.pick({
  label: true,
  description: true,
});

type FormValues = z.infer<typeof formSchema>;

const defaultFormValues = {
  label: "",
  description: "",
};

const FORM_ID = "matter-intake-template-form";

export const AddOrEditMatterIntakeTemplateModal = ({
  editing,
  open,
  setOpen,
  setEditing,
  setSelectedTemplateId,
  template,
  isReadOnly,
}: AddOrEditMatterIntakeTemplateModalProps) => {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);
  const [closeAfterSave, setCloseAfterSave] = useState(false);

  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [isEditingQuestion, setIsEditingQuestion] = useState(false);
  const [selectedQuestion, setSelectedQuestion] =
    useState<MatterIntakeTemplateQuestion>();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultFormValues,
          ...template,
        }
      : defaultFormValues,
    mode: "onSubmit",
  });

  const createMatterIntakeTemplate = useCreateMatterIntakeTemplate();
  const updateMatterIntakeTemplate = useUpdateMatterIntakeTemplate();
  const deleteMatterIntakeTemplate = useDeleteMatterIntakeTemplate();
  const deleteMatterIntakeTemplateQuestion =
    useDeleteMatterIntakeTemplateQuestion();

  const { data: questions, isLoading: isQuestionsLoading } = useQuery({
    ...retrieveMatterIntakeTemplateQuestionsOptions(template?.id),
  });

  const isLoading =
    createMatterIntakeTemplate.isPending ||
    updateMatterIntakeTemplate.isPending ||
    deleteMatterIntakeTemplate.isPending ||
    deleteMatterIntakeTemplateQuestion.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (editing) {
      if (!template) {
        console.error("Active matter intake template is undefined");
        return;
      }
      await updateMatterIntakeTemplate.mutateAsync({
        ...data,
        id: template.id,
        dateModified: template.dateModified,
      });
    } else {
      const result = await createMatterIntakeTemplate.mutateAsync(data);
      setEditing(true);
      setSelectedTemplateId(result.data.id);
    }

    if (closeAfterSave) {
      setOpen(false);
    }
  };

  const handleDelete = async () => {
    if (!template) {
      console.error("Active matter intake template is undefined");
      return;
    }
    await deleteMatterIntakeTemplate.mutateAsync(template.id);
    setIsDeleting(false);
    setOpen(false);
  };

  const handleAddQuestion = () => {
    setIsEditingQuestion(false);
    setSelectedQuestion(undefined);
    setIsQuestionModalOpen(true);
  };

  const handleEditQuestion = (question: MatterIntakeTemplateQuestion) => {
    setIsEditingQuestion(true);
    setSelectedQuestion(question);
    setIsQuestionModalOpen(true);
  };

  const handleQuestionModalClose = () => {
    setIsQuestionModalOpen(false);
    setIsEditingQuestion(false);
    setSelectedQuestion(undefined);
  };

  useEffect(() => {
    if (!open) {
      form.reset(defaultFormValues);
      setCloseAfterSave(false);
    }
  }, [open, form]);

  const canDelete =
    editing &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterIntakeTemplate,
      ACTION_PERMISSION_VALUES.delete,
    );

  const deleteButton = canDelete ? (
    <div>
      <Button
        type="button"
        variant="destructive"
        onClick={() => {
          setIsDeleting(true);
        }}
      >
        Delete
      </Button>
    </div>
  ) : null;

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={editing ? "Edit intake template" : "Create intake template"}
          isDirty={form.formState.isDirty}
          loading={isLoading}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              {deleteButton}
              <div className="flex grow justify-end space-x-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly && (
                  <>
                    <FormSubmitButton
                      submitFormId={FORM_ID}
                      variant="light-primary"
                      loading={isLoading}
                      submitFn={() => {
                        setCloseAfterSave(false);
                      }}
                    />
                    <FormSubmitButton
                      submitFormId={FORM_ID}
                      variant="default"
                      loading={isLoading}
                      submitFn={() => {
                        setCloseAfterSave(true);
                      }}
                      title="Save and close"
                    />
                  </>
                )}
              </div>
            </div>
          }
        >
          <form
            id={FORM_ID}
            className="flex min-h-0 grow flex-col overflow-hidden"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await form.handleSubmit(onSubmit)();
            }}
          >
            <div className="overflow-y-auto">
              <fieldset
                disabled={isReadOnly}
                className={slideoverFieldsetClassName}
              >
                <TextFormField
                  label="Label*"
                  placeholder="Enter intake template label"
                  register={form.register("label")}
                />
                <div className="col-span-2">
                  <TextAreaFormField
                    label="Description"
                    register={form.register("description")}
                  />
                </div>
              </fieldset>

              {editing && (
                <div className="mt-6 p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <Typography variant="heading" size="lg">
                        Template questions
                      </Typography>
                      <Typography
                        variant="subHeading"
                        size="sm"
                        className="mt-1"
                      >
                        Questions that are part of this intake template
                      </Typography>
                    </div>
                    {!isReadOnly && template && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleAddQuestion}
                      >
                        <PlusCircle className="mr-2 h-4 w-4" />
                        Add question
                      </Button>
                    )}
                  </div>

                  {isQuestionsLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((index) => (
                        <Card key={index} className="border-border">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="mb-2 flex items-center gap-2">
                                  <Skeleton className="h-5 w-32" />
                                  <Skeleton className="h-5 w-16" />
                                </div>
                                <Skeleton className="h-4 w-48" />
                              </div>
                              <Skeleton className="h-8 w-8" />
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : questions?.data && questions.data.length > 0 ? (
                    <div className="space-y-3">
                      {questions.data.map(
                        (question: MatterIntakeTemplateQuestion) => (
                          <Card key={question.id} className="border-border">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="mb-2 flex items-center gap-2">
                                    <Typography
                                      variant="heading"
                                      size="md"
                                      className="font-medium"
                                    >
                                      {question.order
                                        ? `${question.order}.`
                                        : ""}{" "}
                                      {question.label}
                                    </Typography>
                                    <Badge
                                      variant="secondary"
                                      className="text-xs"
                                    >
                                      {question.type}
                                    </Badge>
                                  </div>

                                  {Array.isArray(question.options) &&
                                    question.type === "Multiple Choice" && (
                                      <div className="mt-2">
                                        <Typography
                                          variant="subHeading"
                                          size="sm"
                                          className="mb-1"
                                        >
                                          Options:
                                        </Typography>
                                        <div className="flex flex-wrap gap-1">
                                          {question.options.map(
                                            (
                                              option: string,
                                              optionIndex: number,
                                            ) => (
                                              <Badge
                                                key={optionIndex}
                                                variant="outline"
                                                className="text-xs"
                                              >
                                                {option}
                                              </Badge>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </div>
                                {!isReadOnly && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                    type="button"
                                    onClick={() => {
                                      handleEditQuestion(question);
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ),
                      )}
                    </div>
                  ) : (
                    <Card className="border-border border-dashed">
                      <CardContent className="p-6 text-center">
                        <Typography
                          variant="subHeading"
                          size="sm"
                          className="text-muted-foreground"
                        >
                          No questions have been added to this template yet.
                        </Typography>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {!editing && (
                <div className="mt-6 px-6">
                  <Card className="border-border border-dashed bg-blue-50">
                    <CardContent className="p-4">
                      <Typography
                        variant="subHeading"
                        size="sm"
                        className="text-blue-700"
                      >
                        💡 After saving this template, you can add questions to
                        customize the intake process.
                      </Typography>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </form>
        </AppModal>
      </Form>

      {template && (
        <AddOrEditMatterIntakeTemplateQuestionModal
          editing={isEditingQuestion}
          open={isQuestionModalOpen}
          setOpen={handleQuestionModalClose}
          template={template}
          question={selectedQuestion}
          isReadOnly={isReadOnly}
        />
      )}

      <ActionConfirmationModal
        title="Are you sure you want to delete this intake template?"
        open={isDeleting}
        setOpen={setIsDeleting}
        afterActionFn={handleDelete}
      />
    </>
  );
};
